(() => {
  'use strict';

  const ROOT_SELECTOR = '.rfn-doctor-schedule';
  const NAV_SELECTOR =
    '.fc-prev-button, .fc-next-button, .fc-today-button';

  const MOBILE_GRID_WIDTH = 760;
  const mobileQuery = window.matchMedia('(max-width: 699px)');

  const initCalendarNavGuard = (root) => {
    if (!root || root.dataset.rfnCalendarNavGuard === 'true') return;

    root.dataset.rfnCalendarNavGuard = 'true';

    let savedScrollY = 0;
    let guardUntil = 0;
    let restoreTimers = [];

    const clearRestoreTimers = () => {
      restoreTimers.forEach((timer) => window.clearTimeout(timer));
      restoreTimers = [];
    };

    const restoreScroll = () => {
      if (performance.now() > guardUntil) return;

      const currentY =
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        0;

      if (Math.abs(currentY - savedScrollY) > 1) {
        window.scrollTo({
          top: savedScrollY,
          left: 0,
          behavior: 'auto'
        });
      }
    };

    const scheduleRestore = () => {
      clearRestoreTimers();

      [0, 40, 100, 180, 320, 520, 850].forEach((delay) => {
        restoreTimers.push(
          window.setTimeout(() => {
            window.requestAnimationFrame(restoreScroll);
          }, delay)
        );
      });
    };

    const beginGuard = (event) => {
      const target =
        event.target instanceof Element ? event.target : null;

      const button = target?.closest(NAV_SELECTOR);
      if (!button || !root.contains(button)) return;

      savedScrollY =
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        0;

      guardUntil = performance.now() + 1100;
      scheduleRestore();
    };

    root.addEventListener('pointerdown', beginGuard, true);

    root.addEventListener(
      'click',
      (event) => {
        const target =
          event.target instanceof Element ? event.target : null;

        const button = target?.closest(NAV_SELECTOR);
        if (!button || !root.contains(button)) return;

        if (performance.now() > guardUntil) {
          beginGuard(event);
        } else {
          scheduleRestore();
        }
      },
      true
    );

    const observer = new MutationObserver(() => {
      if (performance.now() <= guardUntil) {
        window.requestAnimationFrame(restoreScroll);
      }
    });

    const calendar =
      root.querySelector('.doctor-schedule-wrap') || root;

    observer.observe(calendar, {
      childList: true,
      subtree: true
    });
  };

  const initMobileGridScale = (root) => {
    if (!root || root.dataset.rfnMobileGridScaleInit === 'true') return;

    root.dataset.rfnMobileGridScaleInit = 'true';

    const calendar =
      root.querySelector('.doctor-schedule-wrap');

    if (!calendar) return;

    let frame = 0;
    let timer = 0;
    let resizeObserver = null;

    const clearGridStyles = (harness) => {
      if (!harness) return;

      harness.style.width = '';
      harness.style.maxWidth = '';
      harness.style.position = '';
      harness.style.left = '';
      harness.style.marginLeft = '';
      harness.style.marginRight = '';
      harness.style.transform = '';
      harness.style.transformOrigin = '';
      harness.style.marginBottom = '';
      harness.style.willChange = '';
    };

    const apply = () => {
      frame = 0;

      const harness =
        calendar.querySelector('.fc-view-harness');

      if (!harness) return;

      if (!mobileQuery.matches) {
        clearGridStyles(harness);
        return;
      }

      const availableWidth = Math.max(
        1,
        Math.round(calendar.clientWidth)
      );

      const scale = Math.min(
        1,
        availableWidth / MOBILE_GRID_WIDTH
      );

      /*
        FullCalendar's month GRID gets a real 760px layout width.
        The toolbar/title above it stay native mobile size.
      */
      harness.style.width = `${MOBILE_GRID_WIDTH}px`;
      harness.style.maxWidth = 'none';
      harness.style.position = 'relative';
      harness.style.left = '50%';
      harness.style.marginLeft = `${-(MOBILE_GRID_WIDTH / 2)}px`;
      harness.style.marginRight = '0';
      harness.style.transformOrigin = 'top center';
      harness.style.transform = `scale(${scale})`;
      harness.style.willChange = 'transform';

      /*
        transform:scale does not change document flow height.
        Pull the following Legend upward by the unused raw height.
      */
      const rawHeight = Math.max(
        1,
        Math.round(harness.offsetHeight)
      );

      const visualHeight = rawHeight * scale;
      harness.style.marginBottom =
        `${Math.round(visualHeight - rawHeight)}px`;
    };

    const schedule = (delay = 0) => {
      window.clearTimeout(timer);

      timer = window.setTimeout(() => {
        if (frame) window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(apply);
      }, delay);
    };

    const mutationObserver = new MutationObserver(() => {
      schedule(30);
    });

    mutationObserver.observe(calendar, {
      childList: true,
      subtree: true
    });

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => schedule(10));
      resizeObserver.observe(calendar);
    }

    const onResize = () => schedule(20);

    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onResize);

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', onResize);
    }

    schedule();
    [120, 300, 700, 1400, 2500].forEach((delay) => {
      window.setTimeout(() => schedule(), delay);
    });
  };

  const init = (root) => {
    initCalendarNavGuard(root);
    initMobileGridScale(root);
  };

  const boot = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => boot(),
      { once: true }
    );
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    boot(event.target);
  });
})();
